import {
  faArrowUpFromBracket,
  faDownload,
  faRepeat,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Card,
  CardContent,
  CardMedia,
  DeterminateCircularProgressIndicator,
  IconButton,
  IndeterminateCircularProgressIndicator,
  Text,
} from '@sixui/ui';
import { cardTokens } from '@sixui/ui/components/Card/Card.stylex';
import { circularProgressIndicatorTokens } from '@sixui/ui/components/CircularProgressIndicator/CircularProgressIndicator.stylex';
import { iconButtonTokens } from '@sixui/ui/components/IconButton/IconButton.stylex';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { stateTokens } from '@sixui/ui/themes/base/state.stylex';
import stylex from '@stylexjs/stylex';

import type { IContainerProps } from '~/components/utils/Container';
import type { IAny, IMaybeAsync } from '~/helpers/types';
import { SupportingText } from '~/components/molecules/SupportingText';
import { getFormattedFileSize } from '~/helpers/getFormattedFileSize';
import { commonStyles } from '~/styles/commonStyles';

export type IFileAssetProps = IContainerProps & {
  href?: string;
  media?: React.ReactNode;
  icon?: React.ReactNode;
  name?: string;
  size?: number;
  thumbUrl?: string;
  downloadUrl?: string;
  onDelete?: () => IMaybeAsync<IAny>;
  onReplace?: () => IMaybeAsync<IAny>;
  onRetry?: () => IMaybeAsync<IAny>;
  stale?: boolean;
  loading?: boolean;
  progress?: number;
  supportingText?: string;
  errorText?: string;
  hideMetadata?: boolean;
};

const styles = stylex.create({
  host: {
    display: 'flex',
    flexDirection: 'column',
    width: 130,
    position: 'relative',
  },
  host$withMetadata: {
    minHeight: 180,
  },
  host$withoutMetadata: {
    [cardTokens.containerColor]: 'transparent',
  },
  card$error: {
    [cardTokens.outlineColor]: colorSchemeTokens.error,
  },
  media: {
    position: 'relative',
  },
  media$withMetadata: {
    backgroundColor: colorSchemeTokens.secondaryContainer,
    color: colorSchemeTokens.onSecondaryContainer,
    height: 80,
  },
  media$withoutMetadata: {
    minHeight: 130,
    height: '100%',
    backgroundColor: 'transparent',
    color: colorSchemeTokens.onSurface,
  },
  media$image$unloaded: {
    color: colorSchemeTokens.onSurfaceVariant,
    boxShadow: `inset 0 0 0 2000px color-mix(in srgb, ${colorSchemeTokens.surface}, transparent 33%)`,
  },
  media$content: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    fontSize: 40,
  },
  progressIndicator: {
    [circularProgressIndicatorTokens.color]:
      colorSchemeTokens.onSecondaryContainer,
    fontSize: '2rem',
  },
  content: {
    flexGrow: 1,
    padding: '0.5rem',
    gap: '0.25rem',
    justifyContent: 'flex-start',
  },
  name: {
    flexGrow: 0,
    wordBreak: 'break-word',
  },
  size: {
    flexGrow: 0,
    color: colorSchemeTokens.onSurfaceVariant,
  },
  supportingTextContainer: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'flex-end',
  },
  actions: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    top: -5,
    right: -5,
    zIndex: 1,
  },
  deleteButton: {
    [iconButtonTokens.containerColor]: colorSchemeTokens.secondary,
    [iconButtonTokens.containerColor$disabled]: colorSchemeTokens.secondary,
    [iconButtonTokens.containerOpacity$disabled]: stateTokens.opacity$disabled,
    [iconButtonTokens.stateLayerColor$hover]: colorSchemeTokens.error,
    [iconButtonTokens.stateLayerColor$pressed]: colorSchemeTokens.error,
    [iconButtonTokens.stateLayerOpacity$hover]: '0.8',
    [iconButtonTokens.stateLayerOpacity$pressed]: '1',
    [iconButtonTokens.iconColor]: colorSchemeTokens.onSecondary,
    [iconButtonTokens.iconColor$hover]: colorSchemeTokens.onError,
    [iconButtonTokens.iconColor$focus]: colorSchemeTokens.onError,
    [iconButtonTokens.iconColor$pressed]: colorSchemeTokens.onError,
  },
  iconContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    textAlign: 'center',
  },
  errorText: {
    color: colorSchemeTokens.error,
  },
});

export const FileAsset: React.FC<IFileAssetProps> = (props) => {
  const {
    sx,
    href,
    media,
    icon,
    thumbUrl,
    downloadUrl,
    name,
    size,
    onDelete,
    onReplace,
    onRetry,
    stale,
    loading,
    progress,
    supportingText,
    errorText,
    hideMetadata,
  } = props;

  const hasError = !!errorText;
  const canRetry = !loading && hasError && !!onRetry;
  const canDelete = !!onDelete;
  const canReplace = !!onReplace;
  const loaded = !stale && !loading && !hasError;
  const bottomText = errorText ?? supportingText;

  return (
    <Card
      sx={[
        styles.host,
        hasError ? styles.card$error : null,
        hideMetadata
          ? [
              styles.host$withoutMetadata,
              commonStyles.transparentCheckerboardBackground,
            ]
          : styles.host$withMetadata,
        sx ?? null,
      ]}
      variant="outlined"
      href={href}
    >
      <CardMedia
        sx={[
          styles.media,
          hideMetadata
            ? styles.media$withoutMetadata
            : styles.media$withMetadata,
          !loaded ? styles.media$image$unloaded : null,
        ]}
        src={thumbUrl}
      >
        <div {...stylex.props(styles.media$content)}>
          {/* TODO: fix "React does not recognize the `withLabel` prop on a DOM
          element."" */}
          {loading ? (
            progress ? (
              <DeterminateCircularProgressIndicator
                sx={styles.progressIndicator}
                value={progress}
                withLabel
                // TODO: large size
              />
            ) : (
              <IndeterminateCircularProgressIndicator
                sx={styles.progressIndicator}
                // TODO: large size
              />
            )
          ) : thumbUrl ? null : icon ? (
            hideMetadata ? (
              <div {...stylex.props(styles.iconContainer)}>
                <div {...stylex.props(styles.icon)}>{icon}</div>
                {name && (
                  <Text variant="label" size="md" truncateLines={1}>
                    {name}
                  </Text>
                )}
                {errorText && (
                  <Text variant="body" size="sm" sx={styles.errorText}>
                    {errorText}
                  </Text>
                )}
              </div>
            ) : (
              <div {...stylex.props(styles.icon)}>{icon}</div>
            )
          ) : (
            media
          )}
        </div>
      </CardMedia>

      {hideMetadata ? null : (
        <Text as={CardContent} variant="body" size="sm">
          <Text variant="label" truncate>
            {name}
          </Text>
          {size ? (
            <div {...stylex.props(styles.size)}>
              {getFormattedFileSize(size)}
            </div>
          ) : null}
          {bottomText ? (
            <div {...stylex.props(styles.supportingTextContainer)}>
              <SupportingText
                hasError={hasError}
                icon={
                  hasError ? (
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                  ) : undefined
                }
              >
                {bottomText}
              </SupportingText>
            </div>
          ) : null}
        </Text>
      )}

      {/* TODO: stack actions with overlap */}

      {canDelete || canRetry || canReplace || downloadUrl ? (
        <div {...stylex.props(styles.actions)}>
          {canDelete ? (
            <IconButton
              sx={styles.deleteButton}
              icon={<FontAwesomeIcon icon={faXmark} />}
              onClick={onDelete}
            />
          ) : null}

          {canReplace ? (
            <IconButton
              variant="filled"
              icon={<FontAwesomeIcon icon={faArrowUpFromBracket} />}
              onClick={onReplace}
            />
          ) : null}

          {canRetry ? (
            <IconButton
              variant="filled"
              icon={<FontAwesomeIcon icon={faRepeat} />}
              onClick={onRetry}
            />
          ) : null}

          {downloadUrl ? (
            <IconButton
              variant="filled"
              icon={<FontAwesomeIcon icon={faDownload} />}
              href={downloadUrl}
              target="_blank"
            />
          ) : null}
        </div>
      ) : null}
    </Card>
  );
};
